import {
  isNonEmptyString,
  isNumberString,
  isNonEmptyArray,
  type Validation,
} from './utils';
import {
  EDIT_PANEL_SCOPE,
  EDIT_PANEL_EVENT_TYPE,
} from '../constants/addons';

const BULK_EDIT_EVENTS = [
  'add',
  'set',
  'delete',
  'replace',
];

interface BulkCollectionRef {
  id: string | number;
}

interface BulkDiff {
  data?: unknown;
  event: string;
  eventType: string;
  scope: string;
}

interface ProductBulkFormValues {
  [key: string]: unknown;
  diff: BulkDiff;
  availableCollections: BulkCollectionRef[];
}

const validations: Validation<ProductBulkFormValues>[] = [
  ({ diff, availableCollections }) => {
    const {
      data,
      event,
      eventType,
      scope,
    } = diff;

    const failureResult = {
      diff: 'diff validate failed',
    };

    if (!scope) {
      return failureResult;
    }

    if (!BULK_EDIT_EVENTS.includes(event)) {
      return failureResult;
    }

    if (
      [
        EDIT_PANEL_SCOPE.TITLE,
        EDIT_PANEL_SCOPE.DESCRIPTION,
      ].includes(scope)
      && ![
        EDIT_PANEL_EVENT_TYPE.BEGINNING,
        EDIT_PANEL_EVENT_TYPE.END,
        EDIT_PANEL_EVENT_TYPE.REPLACE,
      ].includes(eventType)
    ) {
      return failureResult;
    }

    if (
      [
        EDIT_PANEL_SCOPE.SHIPPING_WINDOW,
        EDIT_PANEL_SCOPE.CUSTOMIZATION,
      ].includes(scope)
      && ![
        EDIT_PANEL_EVENT_TYPE.ADD,
        EDIT_PANEL_EVENT_TYPE.REMOVE,
        EDIT_PANEL_EVENT_TYPE.REPLACE,
      ].includes(event)
    ) {
      return failureResult;
    }

    if ([
      EDIT_PANEL_SCOPE.TITLE,
      EDIT_PANEL_SCOPE.DESCRIPTION,
    ].includes(scope)) {
      if (typeof data !== 'object') {
        return failureResult;
      }

      const text = data as { find?: unknown; replace?: unknown; text?: unknown };

      if (eventType === EDIT_PANEL_EVENT_TYPE.REPLACE) {
        if (!isNonEmptyString(text.find)) {
          return failureResult;
        }
        if (!text.replace && text.replace !== '') {
          // if replace was not set, it should be an empty string
          return failureResult;
        }
      } else {
        // begin / end

        if (!isNonEmptyString(text.text)) {
          return failureResult;
        }
      }
    } else if (EDIT_PANEL_SCOPE.SHIPPING_WINDOW === scope) {
      if (!Array.isArray(data)) {
        return failureResult;
      }

      const shippingWindowErrors: Record<string, unknown>[] = [];
      const dataValidateSuccess = data.every((d, idx) => {
        const { name, weeks, price } = d;
        if ((idx === data.length - 1) && !name && !weeks && !price) {
          // ignore the last empty row
          return true;
        }

        const error: Record<string, unknown> = {
          rowIndex: idx,
        };

        if (!isNonEmptyString(name)) {
          error.name = 'name is required';
          // return false;
        }

        if (EDIT_PANEL_EVENT_TYPE.REMOVE === event) {
          // prevent check other field when remove choosed
          return true;
        }

        if (!isNumberString(weeks)) {
          error.weeks = 'weeks is required';
          // return false;
        }

        if (!isNumberString(price)) {
          error.price = 'price is required';
          // return false;
        }

        if (Object.keys(error).length > 1) {
          shippingWindowErrors.push(error);
          return false;
        }

        return true;
      });

      if (!dataValidateSuccess) {
        return {
          shippingWindow: shippingWindowErrors,
        };
      }
    } else if (EDIT_PANEL_SCOPE.CUSTOMIZATION === scope) {
      if (!Array.isArray(data)) {
        return failureResult;
      }

      const customizationErrors: Record<string, unknown>[] = [];
      const dataValidateSuccess = data.every((d, idx) => {
        const { name, price } = d;
        if ((idx === data.length - 1) && !name && !price) {
          // ignore the last empty row
          return true;
        }

        const error: Record<string, unknown> = {
          rowIndex: idx,
        };

        if (!isNonEmptyString(name)) {
          error.name = 'name is required';
        }

        if (EDIT_PANEL_EVENT_TYPE.REMOVE === event) {
          // prevent check other field when remove choosed
          return true;
        }

        if (!isNumberString(price)) {
          error.price = 'price is required';
        }

        if (Object.keys(error).length > 1) {
          customizationErrors.push(error);
          return false;
        }

        return true;
      });

      if (!dataValidateSuccess) {
        return {
          customization: customizationErrors,
        };
      }
    } else if (EDIT_PANEL_SCOPE.COLLECTION === scope) {
      if (!isNonEmptyArray(data)) {
        return failureResult;
      }

      // all new collection ids should be available
      const allAvaiable = (data as unknown[]).every((cid) => availableCollections.find((ac) => ac.id === cid));
      if (!allAvaiable) {
        return failureResult;
      }
    }

    return false;
  },
];

const KEYS = [
  'diff',
];

export {
  validations,
  KEYS,
};
